import { fork, call, take, put, all } from 'redux-saga/effects'
import { push } from 'connected-react-router'
import { unauthorizedAction } from '../actions/authActions'
import {
  getAllPressureMonitorsAction,
  getPressureMonitorAction,
  createPressureMonitorAction,
  updatePressureMonitorAction,
  deletePressureMonitorAction,
  getAllAvailablePressureMonitorsBySiteAction,
  getPressureMonitorEditHistoryAction,
  getPressureMonitorAssignmentHistoryAction,
  getPressureMonitorDataAction,
} from '../actions/pressureMonitorsActions'
import {
  showSuccessMessageAction,
  showErrorMessageAction,
} from '../actions/uiActions'
import api from '../services/api'
import { MESSAGES } from '../constants'
import { pickErrorMessage } from '../utils/helpers'
import { createQueryString, parseQueryParams } from '../utils/queryParams'

// handlers

function* handleGetAllPressureMonitorsSaga(qs) {
  try {
    const payload = yield call(api.getAllPressureMonitors, qs)
    yield put(getAllPressureMonitorsAction.success(payload))
  } catch (error) {
    yield put(getAllPressureMonitorsAction.failure(error))
    yield put(showErrorMessageAction(pickErrorMessage(error)))

    if (error.status === 401) {
      yield put(unauthorizedAction())
    }
  }
}

function* handleGetPressureMonitorSaga(id) {
  try {
    const payload = yield call(api.getPressureMonitor, id)
    yield put(getPressureMonitorAction.success(payload))
  } catch (error) {
    yield put(getPressureMonitorAction.failure(error))
    yield put(showErrorMessageAction(pickErrorMessage(error)))

    if (error.status === 401) {
      yield put(unauthorizedAction())
    }
  }
}

function* handleCreatePressureMonitorSaga(pressureMonitor) {
  try {
    const payload = yield call(api.createPressureMonitor, {
      pressureJson: pressureMonitor,
    })

    yield put(createPressureMonitorAction.success(payload))
    yield put(showSuccessMessageAction(MESSAGES.SUCCESSFULLY_CREATED))
    yield put(push(`/inventory/pressure-monitors/${payload.id}`))
  } catch (error) {
    yield put(createPressureMonitorAction.failure(error))
    yield put(showErrorMessageAction(pickErrorMessage(error)))

    if (error.status === 401) {
      yield put(unauthorizedAction())
    }
  }
}

function* handleUpdatePressureMonitorSaga(pressureMonitor) {
  try {
    const payload = yield call(
      api.updatePressureMonitor,
      { pressureJson: pressureMonitor },
      pressureMonitor.id
    )

    yield put(updatePressureMonitorAction.success(payload))
    yield put(showSuccessMessageAction(MESSAGES.SUCCESSFULLY_UPDATED))
    yield put(push(`/inventory/pressure-monitors/${payload.id}`))
  } catch (error) {
    yield put(updatePressureMonitorAction.failure(error))
    yield put(showErrorMessageAction(pickErrorMessage(error)))

    if (error.status === 401) {
      yield put(unauthorizedAction())
    }
  }
}

function* handleDeletePressureMonitorSaga(id) {
  try {
    const confirmDeletion = yield call(global.confirm, MESSAGES.CONFIRM)
    if (confirmDeletion) {
      yield call(api.deletePressureMonitor, id)
      const qs = yield call(
        createQueryString,
        parseQueryParams(global.location)
      )
      yield fork(handleGetAllPressureMonitorsSaga, qs)
      yield put(showSuccessMessageAction(MESSAGES.SUCCESSFULLY_DELETED))
    }
  } catch (error) {
    yield put(deletePressureMonitorAction.failure(error))
    yield put(showErrorMessageAction(pickErrorMessage(error)))

    if (error.status === 401) {
      yield put(unauthorizedAction())
    }
  }
}

function* handleGetAllAvailablePressureMonitorsBySiteSaga(siteSlug) {
  try {
    const payload = yield call(
      api.getAllAvailablePressureMonitorsBySite,
      siteSlug
    )
    yield put(getAllAvailablePressureMonitorsBySiteAction.success(payload))
  } catch (error) {
    yield put(getAllAvailablePressureMonitorsBySiteAction.failure(error))
    yield put(showErrorMessageAction(pickErrorMessage(error)))

    if (error.status === 401) {
      yield put(unauthorizedAction())
    }
  }
}

function* handleGetPressureMonitorEditHistorySaga(id) {
  try {
    const result = yield call(api.getPressureMonitorEditHistory, id)
    yield put(getPressureMonitorEditHistoryAction.success(result))
  } catch (error) {
    yield put(getPressureMonitorEditHistoryAction.failure(error))
    yield put(showErrorMessageAction(pickErrorMessage(error)))

    if (error.status === 401) {
      yield put(unauthorizedAction())
    }
  }
}

function* handleGetPressureMonitorAssignmentHistorySaga(id) {
  try {
    const result = yield call(api.getPressureMonitorAssignmentHistory, id)
    yield put(getPressureMonitorAssignmentHistoryAction.success(result))
  } catch (error) {
    yield put(getPressureMonitorAssignmentHistoryAction.failure(error))
    yield put(showErrorMessageAction(pickErrorMessage(error)))

    if (error.status === 401) {
      yield put(unauthorizedAction())
    }
  }
}

function* handleGetPressureMonitorDataSaga(payload) {
  try {
    const result = yield call(api.getPressureMonitorData, payload)
    yield put(getPressureMonitorDataAction.success(result))
  } catch (error) {
    yield put(getPressureMonitorDataAction.failure(error))
    yield put(showErrorMessageAction(pickErrorMessage(error)))

    if (error.status === 401) {
      yield put(unauthorizedAction())
    }
  }
}

// watchers

function* watchGetAllPressureMonitorsSaga() {
  while (true) {
    const { payload: qs } = yield take(getAllPressureMonitorsAction.REQUEST)
    yield fork(handleGetAllPressureMonitorsSaga, qs)
  }
}

function* watchGetPressureMonitorSaga() {
  while (true) {
    const { payload } = yield take(getPressureMonitorAction.REQUEST)
    yield fork(handleGetPressureMonitorSaga, payload)
  }
}

function* watchCreatePressureMonitorSaga() {
  while (true) {
    const { payload } = yield take(createPressureMonitorAction.REQUEST)
    yield fork(handleCreatePressureMonitorSaga, payload)
  }
}

function* watchUpdatePressureMonitorSaga() {
  while (true) {
    const { payload } = yield take(updatePressureMonitorAction.REQUEST)
    yield fork(handleUpdatePressureMonitorSaga, payload)
  }
}

function* watchDeletePressureMonitorSaga() {
  while (true) {
    const { payload } = yield take(deletePressureMonitorAction.REQUEST)
    yield fork(handleDeletePressureMonitorSaga, payload)
  }
}

function* watchGetAllAvailablePressureMonitorsBySiteSaga() {
  while (true) {
    const { payload } = yield take(
      getAllAvailablePressureMonitorsBySiteAction.REQUEST
    )
    yield fork(handleGetAllAvailablePressureMonitorsBySiteSaga, payload)
  }
}

function* watchGetPressureMonitorEditHistorySaga() {
  while (true) {
    const { payload } = yield take(getPressureMonitorEditHistoryAction.REQUEST)
    yield fork(handleGetPressureMonitorEditHistorySaga, payload)
  }
}

function* watchGetPressureMonitorAssignmentHistorySaga() {
  while (true) {
    const { payload } = yield take(
      getPressureMonitorAssignmentHistoryAction.REQUEST
    )
    yield fork(handleGetPressureMonitorAssignmentHistorySaga, payload)
  }
}

function* watchGetPressureMonitorDataSaga() {
  while (true) {
    const { payload } = yield take(getPressureMonitorDataAction.REQUEST)
    yield fork(handleGetPressureMonitorDataSaga, payload)
  }
}

function* pressureMonitorsSaga() {
  yield all([
    fork(watchGetAllPressureMonitorsSaga),
    fork(watchGetPressureMonitorSaga),
    fork(watchCreatePressureMonitorSaga),
    fork(watchUpdatePressureMonitorSaga),
    fork(watchDeletePressureMonitorSaga),
    fork(watchGetAllAvailablePressureMonitorsBySiteSaga),
    fork(watchGetPressureMonitorEditHistorySaga),
    fork(watchGetPressureMonitorAssignmentHistorySaga),
    fork(watchGetPressureMonitorDataSaga),
  ])
}

export {
  pressureMonitorsSaga as default,
  watchGetAllPressureMonitorsSaga,
  watchGetPressureMonitorSaga,
  watchCreatePressureMonitorSaga,
  watchUpdatePressureMonitorSaga,
  watchDeletePressureMonitorSaga,
  watchGetAllAvailablePressureMonitorsBySiteSaga,
  watchGetPressureMonitorEditHistorySaga,
  watchGetPressureMonitorAssignmentHistorySaga,
  watchGetPressureMonitorDataSaga,
  handleGetAllPressureMonitorsSaga,
  handleGetPressureMonitorSaga,
  handleCreatePressureMonitorSaga,
  handleUpdatePressureMonitorSaga,
  handleDeletePressureMonitorSaga,
  handleGetAllAvailablePressureMonitorsBySiteSaga,
  handleGetPressureMonitorEditHistorySaga,
  handleGetPressureMonitorAssignmentHistorySaga,
  handleGetPressureMonitorDataSaga,
}
